import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'

export interface DeudaPendiente {
  id: string
  cliente_id: string
  servicio_id: string | null
  servicio_nombre: string | null
  concepto: string
  fecha: string
  mes_anio: string | null
  monto_original: number
  monto_aplicado: number
  saldo_pendiente: number
  estado: 'pendiente' | 'parcial' | 'pagado'
}

export interface PagoConSaldo {
  id: string
  cliente_id: string
  concepto: string
  fecha: string
  monto_original: number
  monto_aplicado: number
  saldo_a_favor: number
  estado: 'sin_aplicar' | 'parcial' | 'aplicado_completo'
}

export interface AplicacionPago {
  deuda_id: string
  monto: number
}

/**
 * Traduce un error de aplicación de pagos a algo que el usuario pueda accionar.
 * Antes el diálogo mostraba siempre 'Error al registrar el pago', así que quien lo
 * veía no tenía forma de saber que el problema eran los saldos viejos en pantalla
 * (ORR-260). Los mensajes de la RPC ya vienen redactados; el del trigger no, así que
 * se reescribe. Nunca devuelve un genérico si hay un mensaje real disponible.
 */
export function mensajeErrorAplicacion(error: unknown): string {
  const mensaje =
    typeof error === 'string'
      ? error
      : error && typeof error === 'object' && 'message' in error
        ? String((error as { message: unknown }).message)
        : ''

  if (!mensaje) return 'No se pudo registrar el pago. Recargá la pantalla y volvé a intentar.'

  // Texto crudo de validate_aplicacion_deuda_saldo / validate_aplicacion_pago_saldo
  if (mensaje.includes('excede el monto de la deuda') || mensaje.includes('excede el monto del pago')) {
    return 'Los saldos cambiaron desde que abriste esta pantalla. Se recargaron los datos: revisá los importes y volvé a intentar.'
  }

  return mensaje
}

export interface RepartoFifo {
  /** Lo que se manda a la RPC, en el orden en que se imputa. */
  aplicaciones: AplicacionPago[]
  /** Deudas NO tildadas que el excedente alcanza a cubrir. Vacío si el monto no sobra. */
  excedenteImputado: { deuda: DeudaPendiente; monto: number }[]
  /** Lo que queda después de cubrir toda la deuda abierta: eso sí es pago a cuenta. */
  sobrante: number
}

const aDosDecimales = (n: number): number => Math.round(n * 100) / 100

/**
 * Reparte un pago entre las deudas del cliente (ORR-307).
 *
 * Primero las deudas que el usuario tildó, más viejas primero. Si sobra plata, el reparto
 * NO se detiene: sigue sobre las deudas abiertas que quedaron sin tildar, también en orden
 * de fecha. Solo lo que sobra después de cubrir TODA la deuda abierta queda a favor.
 *
 * Antes el loop iteraba únicamente las tildadas y descartaba el excedente en silencio. Eso
 * contradice cómo imputa el estudio —"los pagos ingresan en orden, primero se absorbe una
 * deuda antes de imputar a la otra"— y en PROD dejó $6.000 flotando el 11/08/2026 mientras
 * la deuda de Agosto estaba abierta y a la vista, sin tildar. El disparador es un aumento
 * de cuota: el desfasaje se arrastra mes a mes y el excedente reaparece en cada pago.
 *
 * Las tildadas van primero AUNQUE alguna sin tildar sea más vieja: el usuario eligió esas
 * explícitamente y su intención manda. El FIFO puro gobierna solo el excedente, que por
 * definición no tenía destino elegido.
 *
 * El pago a cuenta deliberado no pasa por acá: ese va por `esPagoACuenta`, que manda el
 * array vacío y es una funcionalidad declarada de la RPC.
 */
export function repartirPagoFifo(
  deudas: DeudaPendiente[],
  seleccionadas: Set<string>,
  monto: number,
): RepartoFifo {
  const vacio: RepartoFifo = { aplicaciones: [], excedenteImputado: [], sobrante: 0 }
  if (!(monto > 0)) return vacio

  const porFecha = (a: DeudaPendiente, b: DeudaPendiente) =>
    new Date(a.fecha).getTime() - new Date(b.fecha).getTime()
  const conSaldo = (d: DeudaPendiente) => d.saldo_pendiente > 0

  const tildadas = deudas.filter(d => seleccionadas.has(d.id) && conSaldo(d)).sort(porFecha)
  const resto = deudas.filter(d => !seleccionadas.has(d.id) && conSaldo(d)).sort(porFecha)

  const aplicaciones: AplicacionPago[] = []
  const excedenteImputado: { deuda: DeudaPendiente; monto: number }[] = []
  let restante = monto

  const imputar = (deuda: DeudaPendiente): number => {
    // `Math.min` acota al saldo real; el redondeo solo limpia el epsilon del punto flotante.
    // Nunca puede quedar por encima del saldo, así que la RPC no lo rechaza.
    const aAplicar = aDosDecimales(Math.min(restante, deuda.saldo_pendiente))
    if (aAplicar <= 0) return 0
    aplicaciones.push({ deuda_id: deuda.id, monto: aAplicar })
    restante = aDosDecimales(restante - aAplicar)
    return aAplicar
  }

  for (const deuda of tildadas) {
    if (restante <= 0) break
    imputar(deuda)
  }

  for (const deuda of resto) {
    if (restante <= 0) break
    const aplicado = imputar(deuda)
    if (aplicado > 0) excedenteImputado.push({ deuda, monto: aplicado })
  }

  return { aplicaciones, excedenteImputado, sobrante: restante }
}

export const aplicacionesService = {
  /**
   * Obtiene pagos con saldo a favor (sin aplicar o parcialmente aplicados) de un cliente
   */
  async obtenerPagosConSaldo(client: SupabaseClient<Database>, clienteId: string): Promise<PagoConSaldo[]> {
    const { data, error } = await client
      .from('vista_saldos_pagos')
      .select('*')
      .eq('cliente_id', clienteId)
      .gt('saldo_a_favor', 0)
      .order('fecha', { ascending: true })

    if (error) throw error

    return (data ?? []).map(d => ({
      id: d.pago_id!,
      cliente_id: d.cliente_id!,
      concepto: d.concepto!,
      fecha: d.fecha!,
      monto_original: Number(d.monto_original),
      monto_aplicado: Number(d.monto_aplicado),
      saldo_a_favor: Number(d.saldo_a_favor),
      estado: d.estado as PagoConSaldo['estado'],
    }))
  },

  /**
   * Obtiene deudas pendientes de un cliente con sus saldos calculados
   */
  async obtenerDeudasPendientes(client: SupabaseClient<Database>, clienteId: string): Promise<DeudaPendiente[]> {
    // Obtener deudas usando la vista que calcula saldos
    const { data, error } = await client
      .from('vista_saldos_deudas')
      .select('*')
      .eq('cliente_id', clienteId)
      .gt('saldo_pendiente', 0)
      .order('fecha', { ascending: true })

    if (error) throw error

    // Si no hay vista, calcular manualmente
    if (!data || data.length === 0) {
      return this.calcularDeudasPendientesManual(client, clienteId)
    }

    // Obtener nombres de servicios
    const servicioIds = [...new Set(data.map(d => d.servicio_id).filter((id): id is string => !!id))]
    let servicios: { id: string; nombre: string }[] = []

    if (servicioIds.length > 0) {
      const { data: serviciosData } = await client
        .from('servicios')
        .select('id, nombre')
        .in('id', servicioIds)
      servicios = serviciosData ?? []
    }

    const serviciosMap = new Map(servicios.map(s => [s.id, s.nombre]))

    return data.map(d => ({
      id: d.deuda_id!,
      cliente_id: d.cliente_id!,
      servicio_id: d.servicio_id ?? null,
      servicio_nombre: d.servicio_id ? serviciosMap.get(d.servicio_id) ?? 'Servicio desconocido' : null,
      concepto: d.concepto!,
      fecha: d.fecha!,
      mes_anio: d.mes_anio ?? null,
      monto_original: Number(d.monto_original),
      monto_aplicado: Number(d.monto_aplicado),
      saldo_pendiente: Number(d.saldo_pendiente),
      estado: d.estado as DeudaPendiente['estado'],
    }))
  },

  /**
   * Calcula deudas pendientes manualmente (si no existe la vista)
   */
  async calcularDeudasPendientesManual(client: SupabaseClient<Database>, clienteId: string): Promise<DeudaPendiente[]> {
    // Obtener todas las deudas del cliente
    const { data: deudas, error: errorDeudas } = await client
      .from('movimientos')
      .select('id, cliente_id, servicio_id, concepto, fecha, mes_anio, debe')
      .eq('cliente_id', clienteId)
      .eq('tipo', 'Deuda')
      .order('fecha', { ascending: true })

    if (errorDeudas) throw errorDeudas

    // Obtener todas las aplicaciones
    const deudaIds = deudas?.map(d => d.id) ?? []
    if (deudaIds.length === 0) return []

    const { data: aplicaciones } = await client
      .from('movimientos_aplicaciones')
      .select('movimiento_deuda_id, monto_aplicado')
      .in('movimiento_deuda_id', deudaIds)

    // Agrupar aplicaciones por deuda
    const aplicacionesPorDeuda = new Map<string, number>()
    aplicaciones?.forEach(a => {
      const actual = aplicacionesPorDeuda.get(a.movimiento_deuda_id) ?? 0
      aplicacionesPorDeuda.set(a.movimiento_deuda_id, actual + Number(a.monto_aplicado))
    })

    // Obtener nombres de servicios
    const servicioIds = [...new Set(deudas?.map(d => d.servicio_id).filter(Boolean) as string[] ?? [])]
    let servicios: { id: string; nombre: string }[] = []

    if (servicioIds.length > 0) {
      const { data: serviciosData } = await client
        .from('servicios')
        .select('id, nombre')
        .in('id', servicioIds as string[])
      servicios = serviciosData ?? []
    }

    const serviciosMap = new Map(servicios.map(s => [s.id, s.nombre]))

    // Calcular deudas pendientes
    const deudasPendientes: DeudaPendiente[] = []

    for (const deuda of deudas ?? []) {
      const montoOriginal = Number(deuda.debe ?? 0)
      const montoAplicado = aplicacionesPorDeuda.get(deuda.id) ?? 0
      const saldoPendiente = montoOriginal - montoAplicado

      if (saldoPendiente > 0) {
        let estado: DeudaPendiente['estado'] = 'pendiente'
        if (montoAplicado > 0) {
          estado = 'parcial'
        }

        deudasPendientes.push({
          id: deuda.id,
          cliente_id: deuda.cliente_id!,
          servicio_id: deuda.servicio_id,
          servicio_nombre: deuda.servicio_id ? serviciosMap.get(deuda.servicio_id) ?? 'Servicio desconocido' : null,
          concepto: deuda.concepto,
          fecha: deuda.fecha,
          mes_anio: deuda.mes_anio,
          monto_original: montoOriginal,
          monto_aplicado: montoAplicado,
          saldo_pendiente: saldoPendiente,
          estado,
        })
      }
    }

    return deudasPendientes
  },

  /**
   * Imputa pagos a deudas con la RPC `aplicar_pagos`, en una sola transacción.
   *
   * O entran todas las filas o no entra ninguna. La RPC exige `crearMovimiento` y valida los
   * montos contra el saldo real. Devuelve la suma imputada.
   */
  async aplicarSaldosADeudas(
    client: SupabaseClient<Database>,
    filas: { pago_id: string; deuda_id: string; monto: number }[],
    createdBy: string,
  ): Promise<number> {
    if (filas.length === 0) return 0

    const { error } = await client.rpc('aplicar_pagos', { p_filas: filas, p_created_by: createdBy })

    if (error) throw error
    return filas.reduce((sum, f) => sum + f.monto, 0)
  },

  /**
   * Obtiene el detalle de aplicaciones de un pago.
   *
   * `mes_anio` es el período de la obligación cancelada, y es lo que el recibo de pago (ORR-336)
   * muestra en su tabla de imputación.
   */
  async obtenerAplicacionesPago(client: SupabaseClient<Database>, pagoId: string) {
    const { data, error } = await client
      .from('movimientos_aplicaciones')
      .select(`
        id,
        movimiento_deuda_id,
        monto_aplicado,
        created_at,
        created_by,
        movimientos!movimientos_aplicaciones_movimiento_deuda_id_fkey(
          concepto,
          fecha,
          debe,
          mes_anio
        )
      `)
      .eq('movimiento_pago_id', pagoId)
      .order('created_at', { ascending: true })

    if (error) throw error
    return data
  },

  /**
   * Obtiene el detalle de aplicaciones de una deuda
   */
  async obtenerAplicacionesDeuda(client: SupabaseClient<Database>, deudaId: string) {
    const { data, error } = await client
      .from('movimientos_aplicaciones')
      .select(`
        id,
        movimiento_pago_id,
        monto_aplicado,
        created_at,
        created_by,
        movimientos!movimientos_aplicaciones_movimiento_pago_id_fkey(
          concepto,
          fecha,
          haber
        )
      `)
      .eq('movimiento_deuda_id', deudaId)
      .order('created_at', { ascending: true })

    if (error) throw error
    return data
  },

  /**
   * Calcula el estado de una deuda
   */
  async calcularEstadoDeuda(client: SupabaseClient<Database>, deudaId: string): Promise<'pendiente' | 'parcial' | 'pagado'> {
    // Obtener deuda
    const { data: deuda, error: errorDeuda } = await client
      .from('movimientos')
      .select('debe')
      .eq('id', deudaId)
      .single()

    if (errorDeuda) throw errorDeuda

    // Obtener aplicaciones
    const { data: aplicaciones } = await client
      .from('movimientos_aplicaciones')
      .select('monto_aplicado')
      .eq('movimiento_deuda_id', deudaId)

    const montoOriginal = Number(deuda.debe ?? 0)
    const montoAplicado = aplicaciones?.reduce((sum, a) => sum + Number(a.monto_aplicado), 0) ?? 0

    if (montoAplicado === 0) return 'pendiente'
    if (montoAplicado < montoOriginal) return 'parcial'
    return 'pagado'
  },
}
